import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChatStateStoreService } from './chat-state-store/chat-state-store.service';
import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ENVIRONMENT } from 'src/app/tokens';
import { IChatState } from './chat.models';
import { interval, map, Observable, tap } from 'rxjs';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { ProfileService } from 'src/app/features/user/profile/profile.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const chatHidden = { transform: 'translateX(120%)' };
const chatVisible = { transform: 'translateX(0)' };
const timing = '300ms ease-in-out';

const elementHidden = { opacity: 0 };
const elementShown = { opacity: 1 };

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  animations: [
    trigger('openClose', [
      state('open', style(chatVisible)),
      state('closed', style(chatHidden)),
      transition('open <=> closed', animate(timing))

    ]),
    trigger('showHide', [
      transition(':enter', [
        style(elementHidden),
        animate('0s', style(elementShown))
      ]),
      transition(':leave', [
        style(elementShown),
        animate('300ms', style(elementHidden))
      ])
    ])
  ]
})
export class ChatComponent implements OnInit {
  private readonly _chatStateStoreService  = inject(ChatStateStoreService);
  private readonly _environment = inject(ENVIRONMENT);
  private readonly _oidcClientService = inject(OidcClientService);
  private readonly _profileService = inject(ProfileService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _storageService = inject(StorageService);  

  private readonly MINUTE = 1000 * 60;
  private _origin = '';
  private readonly _START_NEW_CHAT_MESSAGE = 'restartPOLLiveChatSession';
  private readonly timer$ = interval(this.MINUTE).pipe(
    takeUntilDestroyed(),
    tap(() => this.chatAvailable.set(this.isChatAvailable()))
  );
  
  protected readonly chatAvailable = signal(false);
  protected readonly iframeSrc_ = signal<SafeResourceUrl>('');
  protected readonly showLiveChat_ = signal(false); 
  protected readonly showLiveChatError_ = signal(false);
  protected readonly showOpenChatBtn_ = signal(true);
  protected readonly sidebarState_ = signal<'open' | 'closed'>('closed');
  
  @HostListener('window:message', ['$event']) 
  handleStartNewChatMessage(event: MessageEvent) {
    const isChatAppOrigin = event.origin === this._origin;
    const isStartNewChatMessage = event.data === this._START_NEW_CHAT_MESSAGE;

    if (isChatAppOrigin && isStartNewChatMessage) {
      this.showLiveChat_.set(false);
    }
  }

  ngOnInit(): void {
    this.chatAvailable.set(this.isChatAvailable());

    this.timer$.subscribe();
  }

  protected openChat(): void {
    this._setAvailableChatState().subscribe();

    this.toggleSidebar();
  }

  protected isChatAvailable(): boolean {
    // all times need to be calculated in EST/EDT
    // Provider Services is available between 7am and 5pm eastern,
    //    and closed between 12pm and 1pm eastern
    //    hidden on weekends
    const now = DateTime.local({ zone: "America/New_York" });

    if (now.isWeekend) {
      return false;
    }

    // an array containing the time ranges that Provider Services is available
    const validTimes = [
      {
        start: DateTime.local({ zone: "America/New_York" }).set({ hour: 8 }).startOf('hour'),
        end: DateTime.local({ zone: "America/New_York" }).set({ hour: 12 }).startOf('hour'),
      },
      {
        start: DateTime.local({ zone: "America/New_York" }).set({ hour: 13 }).startOf('hour'),
        end: DateTime.local({ zone: "America/New_York" }).set({ hour: 17 }).startOf('hour'),
      },
    ];

    let isAvailable = false;

    for (const { start, end } of validTimes) {
      isAvailable ||= !(now < start || end <= now);
    }

    return isAvailable;
  }

  protected toggleSidebar(): void {
    if (this.sidebarState_() === 'open') {
      this.sidebarState_.set('closed');
      this.showOpenChatBtn_.set(true);
    } else {
      this.sidebarState_.set('open');
      this.showOpenChatBtn_.set(false);
    }
  }

  private _setAvailableChatState(): Observable<Partial<IChatState>> {
    return this._profileService.getMyProfile()
      .pipe(
        map((res) => {
          const { firstName, lastName, email } = res.userPreferences;
          const { tpa_client, tpa_div, tpa_id } = JSON.parse(this._storageService.getItem('user-profile'));

          return {
            email,
            firstName,
            lastName,
            nickName: firstName,
            preferredName: firstName,
            tpaClient: tpa_client,
            tpaDiv: tpa_div,
            tpaId: tpa_id
          };
        }),
        tap((res) => this._chatStateStoreService.updatePartialChatState(res))
      );
  }

  protected handleChatTokenAcquired(chatToken: string): void {
    if (chatToken) {
      const authToken = this._oidcClientService.getUser().id_token;
      const environment = (this._environment as any).env;
      const environmentUrlSegment = environment === 'prod' ? '' : environment;
      const origin = `https://chat${environmentUrlSegment}.upmchp.com`;
      const liveChatUrl = `${origin}/#/livechat/${chatToken}?sessionToken=${authToken}`;
      const trustedLiveChatUrl = this._sanitizer.bypassSecurityTrustResourceUrl(liveChatUrl);
  
      this._origin = origin;

      this.iframeSrc_.set(trustedLiveChatUrl);
  
      this.showLiveChat_.set(true);
    } else {
      this.showLiveChatError_.set(true);
    }
  }

  protected handleRetryClick(): void {
    this.showLiveChatError_.set(false);
  }
}
